import axios from "axios";
import { getToken, getTokenMicro } from "../../config/getToken";
import { env } from "../../env";

axios.interceptors.response.use(
  (response) => {
    if (response.data.responseCode === 1016 || response.status === 403) {
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = "/";
    } else {
      return response;
    }
  },
  (error) => {
    return Promise.reject(error);
  },
);

/** Obtiene el listado de compañías.
 * @returns {Promise<*>} Listado de compañías. */
export async function GetCompanies() {
  const token = await getToken();
  return axios({
    method: "GET",
    url: "apiCompany",
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Obtiene los usuarios de una compañía.
 * @param {Object} data - Datos de consulta.
 * @returns {Promise<*>} Usuarios de la compañía. */
export async function GetUsersByCompany(data) {
  const token = await getTokenMicro();
  return axios({
    method: "POST",
    data,
    url: `users/companies`,
    baseURL: env.REACT_APP_API_URL_USERS,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Actualiza los productos contratados de una compañía.
 * @param {Object} data - Datos de actualización.
 * @returns {Promise<*>} Respuesta del servidor. */
export async function UpdateCompany(data) {
  const token = await getTokenMicro();
  return axios({
    method: "PUT",
    url: `contractedProducts`,
    data: data,
    baseURL: env.REACT_APP_API_URL_PRODUCTS,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Obtiene una compañía por su ID.
 * @param {Object} data - Datos con el ID de la compañía.
 * @returns {Promise<*>} Compañía solicitada. */
export async function GetCompanyById(data) {
  const token = await getToken();
  return axios({
    method: "GET",
    url: `apiCompany/${data.companyId}`,
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Obtiene el listado de compañías para filtros.
 * @returns {Promise<*>} Listado de compañías. */
export async function GetFiltersList() {
  const token = await getToken();
  return axios({
    method: "GET",
    url: "companies",
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Crea un producto contratado para una compañía.
 * @param {Object} data - Datos del producto contratado.
 * @returns {Promise<*>} Respuesta del servidor. */
export async function CreateContractedProduct(data) {
  const token = await getTokenMicro();
  return axios({
    method: "POST",
    url: "contractedProducts",
    data: data,
    baseURL: env.REACT_APP_API_URL_PRODUCTS,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Obtiene los productos contratados de una compañía.
 * @param {Object} data - Datos de consulta.
 * @returns {Promise<*>}> Productos contratados. */
export async function GetContractedProduct(data) {
  const token = await getToken();
  return axios({
    method: "GET",
    url: `contractedProduct/${data.eanCompany}`,
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/**
 * Servicio que trae los productos contratados con al menos un usuario disponible
 * @param {} data
 * @returns
 */
export async function GetActiveProduct(data) {
  const token = await getTokenMicro();
  return axios({
    method: "POST",
    url: `contractedProducts/administrations`,
    data: data,
    baseURL: env.REACT_APP_API_URL_PRODUCTS,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Obtiene la vista previa de productos disponibles de una compañía.
 * @param {Object} data - Datos de consulta.
 * @returns {Promise<*>} Productos disponibles. */
export async function GetCompanyProducts(data) {
  const token = await getTokenMicro();
  return axios({
    method: "POST",
    url: `contractedProducts/previews/availables`,
    data: data,
    baseURL: env.REACT_APP_API_URL_PRODUCTS,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Obtiene los productos disponibles por tipo.
 * @param {Object} data - Datos de consulta.
 * @returns {Promise<*>} Productos disponibles. */
export async function GetAvailableProduct(data) {
  const token = await getToken();
  const tokenMicro = await getTokenMicro();

  return axios({
    method: "POST",
    url: "menus/typeProduct/available",
    data: data,
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
      "X-Authorization": tokenMicro,
    },
  });
}

/** Obtiene el tipo de producto del menú nivel 1.
 * @returns {Promise<*>} Tipo de producto. */
export async function GetMenuLevel1() {
  const token = await getToken();

  return axios({
    method: "GET",
    url: "menus/typeProduct",
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Filtra los productos contratados.
 * @param {Object} data - Datos de filtro.
 * @returns {Promise<*>} Productos filtrados. */
export async function ApplyFiltersProduct(data) {
  const token = await getTokenMicro();

  return axios({
    method: "POST",
    url: `contractedProducts/administrations`,
    data: data,
    baseURL: env.REACT_APP_API_URL_PRODUCTS,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Filtra las compañías.
 * @param {Object} data - Datos de filtro.
 * @returns {Promise<*>} Compañías filtradas. */
export async function ApplyFilters(data) {
  const token = await getToken();
  return axios({
    method: "POST",
    url: "apiCompany/filter",
    data: data,
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Filtra los usuarios por compañía.
 * @param {Object} data - Datos de filtro.
 * @returns {Promise<*>} Usuarios filtrados. */
export async function ApplyFiltersDetail(data) {
  const token = await getTokenMicro();
  return axios({
    method: "POST",
    url: "users/companies",
    data: data,
    baseURL: env.REACT_APP_API_URL_USERS,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Crea un nuevo usuario.
 * @param {Object} data - Datos del usuario.
 * @returns {Promise<*>} Respuesta del servidor. */
export async function CreateUsers(data) {
  const token = await getTokenMicro();

  return axios({
    method: "POST",
    url: "users",
    data: data,
    baseURL: env.REACT_APP_API_URL_USERS,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Edita un usuario existente.
 * @param {Object} data - Datos actualizados del usuario.
 * @returns {Promise<*>} Respuesta del servidor. */
export async function EditUsers(data) {
  const token = await getTokenMicro();
  return axios({
    method: "PUT",
    url: `users`,
    data: data,
    baseURL: env.REACT_APP_API_URL_USERS,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Reenvía las credenciales al usuario.
 * @param {Object} data - Datos del usuario.
 * @returns {Promise<*>} Respuesta del servidor. */
export async function ResendCredentials(data) {
  const token = await getToken();
  return axios({
    method: "POST",
    url: "auths/resend/passwords",
    data: data,
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Obtiene los roles por producto.
 * @param {Object} data - Datos de consulta.
 * @returns {Promise<*>} Roles por producto. */
export async function GetRolesByProduct(data) {
  const token = await getToken();
  return axios({
    method: "POST",
    url: "roles/products",
    data: data,
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Obtiene una compañía por su EAN y país.
 * @param {Object} data - Datos con eanCompany y country.
 * @returns {Promise<*>} Compañía solicitada. */
export async function getCompanyByEan(data) {
  const token = await getToken();
  const { eanCompany, country } = data;
  return axios({
    method: "GET",
    url: `companies/providers/${eanCompany}/countries/${country}`,
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Obtiene las compañías para administración.
 * @param {Object} data - Datos de consulta.
 * @returns {Promise<*>} Compañías. */
export async function getCompaniesAdmin(data) {
  const token = await getToken();
  return axios({
    method: "POST",
    url: `companies/administrations`,
    baseURL: env.REACT_APP_API_URL,
    data: data,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Crea una tarjeta de indicador.
 * @param {Object} data - Datos del indicador.
 * @returns {Promise<*>} Respuesta del servidor. */
export async function CreateIndicatorCard(data) {
  const token = await getToken();

  return axios({
    method: "POST",
    url: "cards",
    baseURL: env.REACT_APP_API_URL,
    data: data,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Edita una tarjeta de indicador existente.
 * @param {Object} data - Datos actualizados del indicador.
 * @returns {Promise<*>} Respuesta del servidor. */
export async function EditIndicatorCard(data) {
  const token = await getToken();

  return axios({
    method: "PUT",
    url: "cards",
    baseURL: env.REACT_APP_API_URL,
    data: data,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Filtra las tarjetas de indicadores por tipo y estado.
 * @param {Object} data - Datos de filtro.
 * @returns {Promise<*>} Tarjetas filtradas. */
export async function ApplyFiltersCards(data) {
  const token = await getToken();

  return axios({
    method: "POST",
    url: "cards/typeIndicator/state",
    baseURL: env.REACT_APP_API_URL,
    data: data,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Lista el orden de las tarjetas de un indicador.
 * @param {string} indicator - Identificador del indicador.
 * @returns {Promise<*>}> Orden de tarjetas. */
export async function ListOrderCard(indicator) {
  const token = await getToken();

  return axios({
    method: "GET",
    url: `cards/order/indicators/${indicator}`,
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Obtiene los tipos de indicadores disponibles.
 * @returns {Promise<*>} Tipos de indicadores. */
export async function ListIndicatorType() {
  const token = await getToken();

  return axios({
    method: "GET",
    url: "cards/typeindicators",
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Administra el acceso a los indicadores.
 * @param {Object} data - Datos de administración.
 * @returns {Promise<*>} Resultado de administración. */
export async function AdministrationAccessIndicators(data) {
  const token = await getToken();

  return axios({
    method: "POST",
    url: "cards/administrations",
    baseURL: env.REACT_APP_API_URL,
    data: data,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Guarda la URL de acceso a los indicadores de un proveedor.
 * @param {Object} data - Datos de configuración.
 * @returns {Promise<*>} Respuesta del servidor. */
export async function SaveUrlAccessIndicators(data) {
  const token = await getToken();

  return axios({
    method: "POST",
    url: "cards/indicators/providers/configurations",
    baseURL: env.REACT_APP_API_URL,
    data: data,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Obtiene el listado de tipos de notificaciones activas. */
export async function GetNotificationsTypes() {
  const token = await getToken();

  return axios({
    method: "GET",
    url: "notifications",
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Administra las notificaciones del sistema.
 * @param {Object} data - Datos de administración.
 * @returns {Promise<*>} Notificaciones administradas. */
export async function AdminNotifications(data) {
  const token = await getToken();

  return axios({
    method: "POST",
    url: "notifications/administrations",
    data: data,
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Crea una nueva notificación.
 * @param {Object} data - Datos de la notificación.
 * @returns {Promise<*>} Respuesta del servidor. */
export async function CreateNotifications(data) {
  const token = await getToken();

  return axios({
    method: "POST",
    url: "notifications",
    data: data,
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Edita una notificación existente.
 * @param {Object} data - Datos actualizados de la notificación.
 * @returns {Promise<*>} Respuesta del servidor. */
export async function EditNotifications(data) {
  const token = await getToken();

  return axios({
    method: "PUT",
    url: "notifications/update",
    data: data,
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Descarga las compañías asociadas a una notificación.
 * @param {Object} data - Datos de descarga.
 * @returns {Promise<*>} Compañías descargadas. */
export async function DownloadNotificationsCompanies(data) {
  const token = await getToken();

  return axios({
    method: "POST",
    url: "notifications/downloads",
    data: data,
    baseURL: env.REACT_APP_API_URL,
    validateStatus: null,
    headers: {
      Authorization: token,
    },
  });
}

/** Cambia la compañía de un usuario de soporte.
 * @param {Object} data - Datos del cambio.
 * @returns {Promise<*>} Respuesta del servidor. */
export async function UpdateSupportCompany(data) {
  return axios({
    method: "POST",
    url: `users/supports/changes/companies`,
    data: data,
    baseURL: env.REACT_APP_API_URL_USERS,
    validateStatus: null,
  });
}